import { Fragment, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, Transition } from '@headlessui/react';
import { 
  XMarkIcon, 
  ClipboardDocumentIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  EnvelopeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useSession } from '@supabase/auth-helpers-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { track } from '../lib/track';

interface CalendarShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedYear: number;
  childrenCount: number;
}

export default function CalendarShareModal({ 
  isOpen, 
  onClose, 
  selectedYear,
  childrenCount 
}: CalendarShareModalProps) {
  const session = useSession();
  const queryClient = useQueryClient();
  const userId = session?.user?.id;
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [generateSuccess, setGenerateSuccess] = useState(false);

  // Fetch share token from profile
  const { data: profile, isLoading, refetch } = useQuery(
    ['profile', userId, 'share_token'],
    async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('calendar_share_token')
        .eq('id', userId)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
      return data;
    },
    {
      enabled: !!userId && isOpen,
      retry: 1
    }
  );

  // Update shareToken state when profile data changes
  useEffect(() => {
    if (profile?.calendar_share_token) {
      setShareToken(profile.calendar_share_token);
    } else {
      setShareToken(null);
    }
  }, [profile]);

  // Track modal opened event (PostHog + dataLayer for GTM/Meta)
  useEffect(() => {
    if (isOpen && userId) {
      track('calendar_share_modal_opened', {
        persona: 'parent',
        user_id: userId,
        year: selectedYear,
        children_count: childrenCount
      });
    }
  }, [isOpen, userId, selectedYear, childrenCount]);

  // Generate share URL
  const getShareUrl = (token: string): string => {
    const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;
    return `${baseUrl}/calendar/share/${token}`;
  };

  // Generate share link
  const generateShareLink = async () => {
    if (!userId) return;

    setIsGenerating(true);
    setGenerateSuccess(false);
    try {
      // Generate new token (32-char hex)
      const newToken = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      const { data, error } = await supabase
        .from('profiles')
        .update({ calendar_share_token: newToken })
        .eq('id', userId)
        .select('calendar_share_token')
        .single();

      if (error) throw error;

      if (data?.calendar_share_token) {
        setShareToken(data.calendar_share_token);
        queryClient.invalidateQueries(['profile', userId, 'share_token']);
        
        track('calendar_share_link_generated', {
          persona: 'parent',
          user_id: userId,
          year: selectedYear
        });

        setGenerateSuccess(true);
        // Clear success message after 3 seconds
        setTimeout(() => setGenerateSuccess(false), 3000);
      }
    } catch (error: any) {
      console.error('Error generating share link:', error);
      toast.error(`Failed to generate share link: ${error.message || 'Unknown error'}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // Copy link to clipboard
  const copyLink = async () => {
    if (!shareToken) return;

    const shareUrl = getShareUrl(shareToken);
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
      
      track('calendar_share_link_copied', {
        persona: 'parent',
        user_id: userId,
        share_token: shareToken.substring(0, 8) + '...', // Truncated for privacy
        year: selectedYear
      });

      // Close modal after a brief delay to allow toast to be visible
      setTimeout(() => {
        handleClose();
      }, 100);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy link');
    }
  };

  // Regenerate share link
  const regenerateLink = async () => {
    if (!userId) return;

    setIsRegenerating(true);
    try {
      const oldToken = shareToken;
      const tokenAge = oldToken ? 0 : 0; // Could calculate actual age if needed

      // Generate new token
      const newToken = Array.from(crypto.getRandomValues(new Uint8Array(16)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      const { data, error } = await supabase
        .from('profiles')
        .update({ calendar_share_token: newToken })
        .eq('id', userId)
        .select('calendar_share_token')
        .single();

      if (error) throw error;

      if (data?.calendar_share_token) {
        setShareToken(data.calendar_share_token);
        queryClient.invalidateQueries(['profile', userId, 'share_token']);
        
        track('calendar_share_token_regenerated', {
          persona: 'parent',
          user_id: userId,
          previous_token_age_days: tokenAge
        });

        toast.success('New share link generated! The old link is now invalid.');
      }
    } catch (error: any) {
      console.error('Error regenerating share link:', error);
      toast.error(`Failed to regenerate share link: ${error.message || 'Unknown error'}`);
    } finally {
      setIsRegenerating(false);
    }
  };

  // Revoke share link
  const revokeLink = async () => {
    if (!userId) return;

    setIsRevoking(true);
    setShowRevokeConfirm(false);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ calendar_share_token: null })
        .eq('id', userId);

      if (error) throw error;

      setShareToken(null);
      queryClient.invalidateQueries(['profile', userId, 'share_token']);

      track('calendar_share_revoked', {
        persona: 'parent',
        user_id: userId,
        token_age_days: 0 // Could calculate if needed
      });

      toast.success('Share link revoked. The link is now disabled.');
      
      // Close modal after a brief delay to allow toast to be visible
      setTimeout(() => {
        handleClose();
      }, 100);
    } catch (error: any) {
      console.error('Error revoking share link:', error);
      toast.error(`Failed to revoke share link: ${error.message || 'Unknown error'}`);
    } finally {
      setIsRevoking(false);
    }
  };

  // Share via email (mailto:)
  const shareViaEmail = () => {
    if (!shareToken) return;

    const shareUrl = getShareUrl(shareToken);
    const subject = encodeURIComponent("Check out my kids' camp schedule on CampWhiz");
    const body = encodeURIComponent(
      `Hi!\n\nI wanted to share my kids' camp schedule with you. You can view it here:\n\n${shareUrl}\n\nThis is a read-only view of our ${selectedYear} camp schedule. Let me know if you have any questions!\n\nBest regards`
    );

    const mailtoUrl = `mailto:${emailRecipients || ''}?subject=${subject}&body=${body}`;
    window.location.href = mailtoUrl;

    track('calendar_share_email_sent', {
      persona: 'parent',
      user_id: userId,
      recipient_count: emailRecipients.split(',').filter(e => e.trim()).length,
      method: 'mailto',
      has_custom_message: !!emailMessage
    });

    setShowEmailForm(false);
    setEmailRecipients('');
    setEmailMessage('');
  };

  const handleClose = () => {
    setShowEmailForm(false);
    setShowAdvanced(false);
    setShowRevokeConfirm(false);
    setEmailRecipients('');
    setEmailMessage('');
    onClose();
  };

  const shareUrl = shareToken ? getShareUrl(shareToken) : '';

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 backdrop-blur-sm transition-opacity z-[9998]" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto z-[9999]">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                      Share Calendar
                    </Dialog.Title>

                    {isLoading ? (
                      <div className="animate-pulse space-y-4">
                        <div className="h-10 bg-gray-200 rounded"></div>
                        <div className="h-10 bg-gray-200 rounded"></div>
                      </div>
                    ) : !shareToken ? (
                      // Initial state: No share link exists
                      <div className="space-y-4">
                        <p className="text-sm text-gray-600">
                          Create a shareable link to your calendar. Anyone with the link can view your {selectedYear} camp schedule (read-only).
                        </p>
                        <button
                          onClick={generateShareLink}
                          disabled={isGenerating}
                          className="btn btn-primary w-full inline-flex items-center justify-center gap-2"
                        >
                          {isGenerating ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Generating...
                            </>
                          ) : generateSuccess ? (
                            <>
                              <CheckCircleIcon className="h-5 w-5" />
                              Share Link Generated!
                            </>
                          ) : (
                            <>
                              <LinkIcon className="h-5 w-5" />
                              Generate Share Link
                            </>
                          )}
                        </button>
                      </div>
                    ) : (
                      // Active state: Share link exists
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Share Link
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              readOnly
                              value={shareUrl}
                              className="flex-1 form-field px-3 py-2 rounded-lg border border-gray-300 bg-gray-50 text-sm"
                            />
                            <button
                              onClick={copyLink}
                              className="btn btn-secondary inline-flex items-center gap-2 px-4"
                            >
                              <ClipboardDocumentIcon className="h-5 w-5" />
                              Copy
                            </button>
                          </div>
                        </div>

                        <p className="text-xs text-gray-500">
                          Anyone with this link can view your calendar (read-only)
                        </p>

                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setShowEmailForm(true);
                              track('calendar_share_email_opened', {
                                persona: 'parent',
                                user_id: userId
                              });
                            }}
                            className="btn btn-primary flex-1 inline-flex items-center justify-center gap-2"
                          >
                            <EnvelopeIcon className="h-5 w-5" />
                            Share via Email
                          </button>
                        </div>

                        {/* Email Form */}
                        {showEmailForm && (
                          <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Recipient Email(s)
                              </label>
                              <input
                                type="text"
                                value={emailRecipients}
                                onChange={(e) => setEmailRecipients(e.target.value)}
                                placeholder="email@example.com, friend@example.com"
                                className="form-field w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Separate multiple emails with commas
                              </p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Optional Message
                              </label>
                              <textarea
                                value={emailMessage}
                                onChange={(e) => setEmailMessage(e.target.value)}
                                placeholder="Add a personal message..."
                                rows={3}
                                className="form-field w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
                              />
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={shareViaEmail}
                                disabled={!emailRecipients.trim()}
                                className="btn btn-primary flex-1"
                              >
                                Send Email
                              </button>
                              <button
                                onClick={() => {
                                  setShowEmailForm(false);
                                  setEmailRecipients('');
                                  setEmailMessage('');
                                }}
                                className="btn btn-secondary"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Advanced Options */}
                        <div className="border-t border-gray-200 pt-4">
                          <button
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            className="w-full flex items-center justify-between text-sm font-medium text-gray-700 hover:text-gray-900"
                          >
                            <span>Advanced Options</span>
                            {showAdvanced ? (
                              <ChevronUpIcon className="h-5 w-5" />
                            ) : (
                              <ChevronDownIcon className="h-5 w-5" />
                            )}
                          </button>

                          {showAdvanced && (
                            <div className="mt-3 space-y-3">
                              <button
                                onClick={regenerateLink}
                                disabled={isRegenerating}
                                className="btn btn-secondary w-full inline-flex items-center justify-center gap-2"
                              >
                                {isRegenerating ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                                    Regenerating...
                                  </>
                                ) : (
                                  <>
                                    <ArrowPathIcon className="h-5 w-5" />
                                    Regenerate Link
                                  </>
                                )}
                              </button>
                              <p className="text-xs text-gray-500">
                                Creates a new link and invalidates the old one
                              </p>

                              <button
                                onClick={() => setShowRevokeConfirm(true)}
                                disabled={isRevoking}
                                className="btn w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white"
                              >
                                {isRevoking ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Revoking...
                                  </>
                                ) : (
                                  <>
                                    <ExclamationTriangleIcon className="h-5 w-5" />
                                    Revoke Sharing
                                  </>
                                )}
                              </button>
                              <p className="text-xs text-gray-500">
                                Permanently disables the share link
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Revoke Confirmation Modal - rendered via portal outside Dialog stacking context */}
                        {showRevokeConfirm && typeof document !== 'undefined' && createPortal(
                          <div className="fixed inset-0 flex items-center justify-center p-4 z-[10001]" style={{ zIndex: 10001 }}>
                            <div 
                              className="fixed inset-0 bg-black/50" 
                              style={{ zIndex: 10000 }}
                              onClick={() => setShowRevokeConfirm(false)} 
                            />
                            <div 
                              className="relative bg-white rounded-lg p-6 max-w-sm w-full shadow-xl" 
                              style={{ zIndex: 10001 }}
                            >
                              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                Revoke Share Link?
                              </h3>
                              <p className="text-sm text-gray-600 mb-4">
                                This will permanently disable the share link. Anyone with the link will no longer be able to view your calendar.
                              </p>
                              <div className="flex gap-3">
                                <button
                                  onClick={revokeLink}
                                  className="btn flex-1 bg-red-600 hover:bg-red-700 text-white"
                                >
                                  Revoke
                                </button>
                                <button
                                  onClick={() => setShowRevokeConfirm(false)}
                                  className="btn btn-secondary flex-1"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>,
                          document.body
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

